/**
 * MiEquipoPanel — GEN-AG-001: «Mi equipo». Una tarjeta por ficha de agente en
 * vigor: foto (iniciales si no hay), nombre, departamento, «Lidera» y estado.
 * Los inactivos también se ven (GEN-281: mismo tablero que «Tu equipo»). Solo admin: el ítem lo proyecta el kernel y la
 * puerta real es GET /v1/agentes (403 al no-admin).
 *
 * La foto sale de la ficha (miniatura, si no la foto) y se pide al kernel con
 * el token; sin foto, AgentAvatar pinta las iniciales.
 *
 * GEN-AG-002: pulsar una tarjeta abre la ficha del agente (solo lectura).
 */

import { useEffect, useState } from 'react'
import { crearAgente, listAgentes } from '../api/client.js'
import TeamBoard from './TeamBoard.jsx'
import PermisosAgentesPanel from './PermisosAgentesPanel.jsx'
import { useSession } from '../context/SessionContext.jsx'
import FichaAgente, { useImagenAgente } from './FichaAgente.jsx'

// GEN-281: misma tarjeta que «Tu equipo» (team-card) con foto de retrato entera
// (contain), la marca Activo/Inactivo y, si está inactivo, la foto desenfocada.
function TarjetaAgente({ agente, onAbrir }) {
  // GEN-281: la foto COMPLETA de la ficha; la miniatura (recorte de cara) solo
  // si no hay foto. El hueco toma la proporción real de la imagen al cargar.
  const foto = useImagenAgente(agente.id, agente.foto || agente.miniatura)
  const [proporcion, setProporcion] = useState(null)
  const activo = agente.estado === 'activo'
  return (
    <button
      type="button"
      className={`team-card team-card--equipo${activo ? '' : ' team-card--inactivo'}`}
      onClick={() => onAbrir(agente.id)}
    >
      <span className="team-card__photo" style={proporcion ? { aspectRatio: proporcion } : undefined}>
        <span className="team-card__photo-ini">{iniciales(agente.nombre)}</span>
        {foto && (
          <img
            src={foto}
            alt=""
            decoding="async"
            onLoad={e => {
              const { naturalWidth: w, naturalHeight: h } = e.currentTarget
              if (w && h) setProporcion(Math.min(1, Math.max(0.5, w / h)))
            }}
            onError={e => { e.currentTarget.style.display = 'none' }}
          />
        )}
      </span>
      <span className="team-card__body">
        <span className="team-card__name">{agente.nombre}</span>
        {agente.cargo && <span className="team-card__role">{agente.cargo}</span>}
        <span className="team-card__chips">
          {agente.lidera && <span className="team-card__lidera">Lidera</span>}
          <span className={`team-card__estado team-card__estado--${activo ? 'activo' : 'inactivo'}`}>
            {activo ? 'Activo' : 'Inactivo'}
          </span>
        </span>
      </span>
    </button>
  )
}

function iniciales(nombre) {
  return (nombre ?? '').split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
}

const VACIO = { id: '', nombre: '', departamento: '', cargo: '', perfil: '', ambito: '', instrucciones: '', caracter: '', comunicacion: '' }

// GEN-AG-007: formulario mínimo de alta. El agente nace inactivo; los textos,
// si se rellenan, quedan como propuesta. Sin validaciones más allá de las del kernel.
function NuevoAgente({ onCreado, onCancelar }) {
  const [form, setForm] = useState(VACIO)
  const [error, setError] = useState(null)
  const [guardando, setGuardando] = useState(false)
  const campo = k => ({ value: form[k], onChange: e => setForm({ ...form, [k]: e.target.value }) })

  async function guardar(e) {
    e.preventDefault()
    setGuardando(true)
    setError(null)
    try {
      await crearAgente(form)
      onCreado()
    } catch (err) {
      setError(err?.status === 409 ? 'Ya hay un agente con ese id.' : (err?.message || 'No se ha podido crear el agente.'))
      setGuardando(false)
    }
  }

  return (
    <form className="equipo-nuevo" onSubmit={guardar}>
      <h3>Nuevo agente</h3>
      <label>Id <input required pattern="[a-z0-9][a-z0-9\-]*" {...campo('id')} /></label>
      <label>Nombre <input required {...campo('nombre')} /></label>
      <label>Departamento <input required {...campo('departamento')} /></label>
      <label>Cargo <input {...campo('cargo')} /></label>
      <label>Perfil <textarea rows={2} {...campo('perfil')} /></label>
      <label>Ámbito <textarea rows={2} {...campo('ambito')} /></label>
      <label>Cómo trabaja <textarea rows={2} {...campo('instrucciones')} /></label>
      <label>Carácter <textarea rows={2} {...campo('caracter')} /></label>
      <label>Comunicación por defecto <textarea rows={2} {...campo('comunicacion')} /></label>
      {error && <p className="equipo-estado" role="alert">{error}</p>}
      <div className="equipo-nuevo__acciones">
        <button type="submit" disabled={guardando}>Crear agente</button>
        <button type="button" onClick={onCancelar} disabled={guardando}>Cancelar</button>
      </div>
    </form>
  )
}

export default function MiEquipoPanel({ subId = null, agenteInicial = null }) {
  const { capabilities } = useSession()
  // GEN-PX-014: «Perfiles» (mapa de permisos, GEN-179) es provisional; sale si el kernel emite `agentes-permisos`.
  const verPerfiles = !!capabilities?.menu?.some(m => m.id === 'agentes-permisos' && m.visible && m.enabled)
  const [datos, setDatos] = useState(null)
  const [abierto, setAbierto] = useState(agenteInicial)
  const [creando, setCreando] = useState(false)

  // Se recarga al volver de una ficha: si se puso en vigor, la tarjeta muestra
  // ya la foto y los datos nuevos (GEN-AG-005).
  useEffect(() => {
    if (abierto || creando) return undefined
    let vivo = true
    listAgentes()
      .then(d => vivo && setDatos(d))
      .catch(() => vivo && setDatos({ error: 'No se ha podido cargar el equipo.' }))
    return () => { vivo = false }
  }, [abierto, creando])

  if (abierto) return <FichaAgente agenteId={abierto} onVolver={() => setAbierto(null)} />

  // GEN-PX-023: Equipo y Perfiles son entradas del carril contextual; `subId` dice cuál abrió (Equipo por defecto).
  // Forma que espera el tablero (la de `agents_visibles`): departamento en texto.
  if (verPerfiles && subId === 'agentes-permisos') return (
    <section className="mec-visor">
      <h2>Agentes</h2>
      <PermisosAgentesPanel />
    </section>
  )

  const agentes = (datos?.agentes ?? []).map(a => ({
    ...a, departamento: a.departamento?.nombre || '', lidera: a.departamento?.lidera === true, rol: a.cargo,
  }))
  return (
    <section className="mec-visor">
      <h2>Agentes</h2>
      {creando ? (
        <NuevoAgente onCreado={() => setCreando(false)} onCancelar={() => setCreando(false)} />
      ) : (
        <button type="button" className="equipo-nuevo__boton" onClick={() => setCreando(true)}>Nuevo agente</button>
      )}
      {!datos && <p className="equipo-estado">Cargando…</p>}
      {datos?.error && <p className="equipo-estado" role="alert">{datos.error}</p>}
      {datos && !datos.error && agentes.length === 0 && (
        <p className="equipo-estado">Todavía no hay fichas de agente.</p>
      )}
      <TeamBoard
        agents={agentes}
        ariaLabel="Agentes del equipo"
        renderCard={a => <TarjetaAgente key={a.id} agente={a} onAbrir={setAbierto} />}
      />
    </section>
  )
}
